import { useEffect, useMemo, useRef, useState } from 'react';
import { useScrollHeader } from '../utils/hooks';
import ThemeSwitcher from './ThemeSwitcher';

const TopMenu = ({
  user,
  activeView,
  onLogout,
  onCreateContent,
  onGoToBrowse,
  onGoToRBAC,
  onGoToColleges,
  onGoToContact,
  onGoToAbout,
  theme,
  onThemeChange
}) => {
  const [openMenu, setOpenMenu] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  const [isHeaderVisible, setIsHeaderVisible] = useScrollHeader();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 720);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const canCreate = Boolean(onCreateContent) && ['super_user', 'college_admin', 'teacher'].includes(user.role);
  const canManageContent = ['super_user', 'college_admin', 'teacher'].includes(user.role);
  const canManageAccounts = ['super_user', 'college_admin'].includes(user.role);
  const canManageCollegeAccess = ['super_user', 'college_admin', 'teacher'].includes(user.role);

  const navGroups = useMemo(() => {
    const workspaceItems = [
      canCreate && {
        key: 'create',
        label: 'Create Content',
        icon: 'fa-file-circle-plus',
        action: () => onCreateContent()
      }
    ].filter(Boolean);

    const accountItems = [
      onGoToRBAC && canManageAccounts && {
        key: 'rbac',
        label: 'User Access',
        icon: 'fa-shield-halved',
        action: onGoToRBAC
      },
      onGoToColleges && canManageCollegeAccess && {
        key: 'colleges',
        label: 'College Management',
        icon: 'fa-school',
        action: onGoToColleges
      },
      onGoToAbout && {
        key: 'about',
        label: 'About Study Centro',
        icon: 'fa-circle-info',
        action: onGoToAbout
      }
    ].filter(Boolean);

    return { workspaceItems, accountItems };
  }, [
    canCreate,
    canManageAccounts,
    canManageCollegeAccess,
    canManageContent,
    onCreateContent,
    onGoToRBAC,
    onGoToColleges,
    onGoToAbout
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenu('');
        setMobileOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpenMenu('');
        setMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const runAction = (action) => {
    setOpenMenu('');
    setMobileOpen(false);
    action();
  };

  const isActive = (keys) => keys.includes(activeView);
  const userInitial = user.name?.trim()?.charAt(0)?.toUpperCase() || 'G';

  const renderDropdown = (menuKey, label, icon, items) => {
    if (items.length === 0) return null;
    const active = isActive(items.map(item => item.key));
    const expanded = openMenu === menuKey;

    return (
      <div className="top-menu-dropdown">
        <button
          type="button"
          className={`top-menu-link${active ? ' active' : ''}`}
          aria-haspopup="menu"
          aria-expanded={expanded}
          onClick={() => setOpenMenu(expanded ? '' : menuKey)}
        >
          <i className={`fas ${icon}`} />
          <span>{label}</span>
          <i className="fas fa-chevron-down top-menu-chevron" />
        </button>

        {expanded && (
          <div className="dropdown-menu" role="menu">
            {items.map(item => (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                className={`dropdown-item${activeView === item.key ? ' active' : ''}`}
                onClick={() => runAction(item.action)}
              >
                <i className={`fas ${item.icon}`} />
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <header className={`top-menu${!isHeaderVisible ? ' header-hidden' : ''}`} ref={menuRef}>
      <button
        type="button"
        className="top-menu-brand"
        onClick={onGoToBrowse}
        aria-label="Go to browse"
      >
        <span className="top-menu-logo"><i className="fas fa-graduation-cap" /></span>
        <span className="top-menu-brand-text">
          <strong>Study Centro</strong>
          <small>Learning workspace</small>
        </span>
      </button>

      {isMobile && (
        <div className="top-menu-mobile-actions">
          <ThemeSwitcher 
            theme={theme} 
            onThemeChange={onThemeChange} 
            compact={true} 
            showLabel={false} 
          />
          <button 
            className="mobile-header-toggle" 
            onClick={() => setIsHeaderVisible(false)} 
            title="Hide Navigation"
          >
            <i className="fas fa-chevron-up" />
          </button>
          <button
            type="button"
            className="top-menu-mobile-toggle"
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(prev => !prev)}
          >
            <i className={`fas ${mobileOpen ? 'fa-times' : 'fa-bars'}`} />
          </button>
        </div>
      )}

      <nav className={`top-menu-nav${mobileOpen ? ' open' : ''}`} aria-label="Primary navigation">
        {onGoToBrowse && (
          <button
            type="button"
            className={`top-menu-link${activeView === 'browse' || activeView === 'test' ? ' active' : ''}`}
            onClick={() => runAction(onGoToBrowse)}
          >
            <i className="fas fa-compass" />
            <span>Browse</span>
          </button>
        )}

        {renderDropdown('workspace', 'Workspace', 'fa-layer-group', navGroups.workspaceItems)}
        {renderDropdown('accounts', 'Accounts', 'fa-user-shield', navGroups.accountItems)}
        
        {onGoToContact && (
          <button
            type="button"
            className={`top-menu-link${activeView === 'contact' ? ' active' : ''}`}
            onClick={() => runAction(onGoToContact)}
          >
            <i className="fas fa-envelope" />
            <span>Contact</span>
          </button>
        )}

        <ThemeSwitcher 
          theme={theme} 
          onThemeChange={onThemeChange} 
          showLabel={true} 
        />
      </nav>

      <div className="top-menu-user">
        <div className="top-menu-avatar" aria-hidden="true">{userInitial}</div>
        <div className="top-menu-user-meta">
          <span className="top-menu-name">{user.name}</span>
          <span className="top-menu-role">{user.role === 'guest' ? 'Guest access' : user.role?.replace('_', ' ') || ''}</span>
        </div>
        <button className="top-menu-logout" type="button" onClick={onLogout}>
          <i className={`fas ${user.role === 'guest' ? 'fa-right-to-bracket' : 'fa-right-from-bracket'}`} />
          <span>{user.role === 'guest' ? 'Login' : 'Logout'}</span>
        </button>
      </div>
    </header>
    
    {isMobile && !isHeaderVisible && (
      <div className="collapsed-mobile-bar">
        <div className="collapsed-brand">
          <i className="fas fa-graduation-cap" />
          <span>Study Centro</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ThemeSwitcher 
            theme={theme} 
            onThemeChange={onThemeChange} 
            compact={true} 
            showLabel={false} 
          />
          <button 
            className="mobile-header-toggle" 
            onClick={() => setIsHeaderVisible(true)} 
            title="Show Navigation"
          >
            <i className="fas fa-chevron-down" />
          </button>
        </div>
      </div>
    )}
  </>
);
};

export default TopMenu;
